import { useState, useEffect, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import {
  Box,
  Text,
  Flex,
  Input,
  Button,
  useToast,
  IconButton,
  Switch,
  Textarea,
  Image,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Tag,
  SimpleGrid,
  Select,
} from '@chakra-ui/react';
import { ChevronDownIcon, SmallCloseIcon, SearchIcon, CheckIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { updateAccount, createAccount, fetchCurrencies, fetchSources, createSource, getReferenceData } from '../../api/endpointsBackend';
import { isDebtAccount } from '../../utils/accountTypes';
import { parseSignedAmount } from '../../utils/amountParser';
import { readableInk } from '../../utils/colorContrast';
import EmojiPickerField from '../shared/EmojiPickerField';
import { CurrencySheet, SourceSheet } from '../phone/PhonePickers';
import { PARENT_TYPE } from '../../utils/subAccounts';

// Two-level account type hierarchy — values are account_type strings
// Labels translated at render via t('categories.X') and t('types.X')
// The gradient each subtype paints its card with, as exact hexes rather than
// Chakra colour-scheme names. The scheme names drifted from what was drawn —
// a savings account came out blue where the mockup draws it green, and six of
// the eight disagreed — and naming a scheme cannot express `cash`, whose
// colour is near-black. These are the values from the account artboard.
const GROUP_CATEGORIES = [
  {
    labelKey: 'Banks',
    groups: [
      { value: 'credit',   labelKey: 'credit',   from: '#F0916A', to: '#EF7547' },
      { value: 'checking', labelKey: 'checking', from: '#5B9BF3', to: '#3B82F6' },
      { value: 'saving',   labelKey: 'saving',   from: '#4ADE80', to: '#22C55E' },
    ],
  },
  {
    labelKey: 'Investments',
    groups: [
      { value: 'stock',      labelKey: 'stock',      from: '#C084FC', to: '#A855F7' },
      { value: 'investment', labelKey: 'investment', from: '#A5B4FC', to: '#6366F1' },
      { value: 'funds',      labelKey: 'funds',      from: '#5EEAD4', to: '#14B8A6' },
    ],
  },
  {
    labelKey: 'Assets',
    groups: [
      { value: 'cash', labelKey: 'cash', from: '#3D3D3D', to: '#1A1A1A' },
      { value: 'lend', labelKey: 'lend', from: '#FB7185', to: '#F43F5E' },
    ],
  },
];

// The institution's bundled logo, falling back to a first-letter avatar.
//
// Every seeded source has an asset, so the avatar is for workspace-custom
// sources (which have none) and for an asset that fails to load. It replaced a
// per-source emoji column: with real logos in place the emoji had no job, and a
// letter derived from the name needs no stored data at all.
export function SourceGlyph({ source, boxSize = '16px' }) {
  if (!source) return null;
  const initial = (source.name || '?').trim().charAt(0).toUpperCase();
  const avatar = (
    <Flex
      boxSize={boxSize}
      align="center"
      justify="center"
      flexShrink={0}
      borderRadius="sm"
      bg="redesign.bg.hover"
      color="redesign.text.muted"
      fontSize="2xs"
      fontWeight={600}
      lineHeight="1"
    >
      {initial}
    </Flex>
  );
  if (!source.logo_url) return avatar;
  return (
    <Image
      src={source.logo_url}
      alt=""
      boxSize={boxSize}
      flexShrink={0}
      objectFit="contain"
      borderRadius="sm"
      fallback={avatar}
    />
  );
}

// MI-304: picks a Source *entity* from the catalog (seeded institutions + this
// workspace's custom ones), rather than typing a free-text name.
//
// Follows the house searchable-dropdown pattern (see CustomDropdown in
// MovementDetailPanel): the trigger shows the current value and the search box
// lives INSIDE the popover. Putting the text input in the trigger instead looks
// tidier but is unusable — Chakra moves focus into the popover when it opens,
// so keystrokes never reach a trigger-input.
function SourcePicker({
  valueId, valueName, sources, onSelect, onCreate, boxed = false, noAutoFocus = false,
}) {
  const { t } = useTranslation('accounts');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const searchRef = useRef(null);

  // Focus the search box whenever the popover opens. Chakra's
  // `initialFocusRef` is not enough here: in create mode the account-name
  // field is auto-focused and wins the race, so every keystroke landed in the
  // NAME field while the dropdown just sat there looking broken. The timeout
  // puts this after Chakra's own focus management.
  useEffect(() => {
    // On a phone nothing takes focus on open: iOS would raise the keyboard
    // before the person has tapped anything.
    if (!isOpen || noAutoFocus) return undefined;
    // Two frames, not a timeout: the popover must have painted before the input
    // is focusable — focus() on a not-yet-visible element silently no-ops, which
    // is why Chakra's own initialFocusRef doesn't work here either.
    let inner;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => searchRef.current?.focus());
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [isOpen, noAutoFocus]);

  const selected = useMemo(
    () => sources.find(s => s.id === valueId) || null,
    [sources, valueId]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter(s => s.name.toLowerCase().includes(q));
  }, [search, sources]);

  // Offer to create only when nothing in the catalog already carries that name.
  const canCreate =
    search.trim().length > 0 &&
    !sources.some(s => s.name.toLowerCase() === search.trim().toLowerCase());

  const close = () => { onClose(); setSearch(''); };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await onCreate(search.trim());
      close();
    } finally {
      setCreating(false);
    }
  };

  const triggerTag = (content) => (
    <Tag size="md" bg="redesign.bg.card" color="redesign.text" variant="subtle" borderRadius="full" px={3} py={1}>
      <Flex align="center" gap={1}>{content}</Flex>
    </Tag>
  );

  const boxedLabel = (
    <>
      <Flex align="center" gap={2} minW={0}>
        {selected && <SourceGlyph source={selected} boxSize="18px" />}
        <Text
          fontSize={noAutoFocus ? '16px' : 'sm'}
          color={selected || valueName ? 'redesign.text' : 'redesign.text.muted'}
          noOfLines={1}
        >
          {selected?.name || valueName || t('detail.fields.sourcePlaceholder')}
        </Text>
      </Flex>
      <ChevronDownIcon color="redesign.text.muted" boxSize={4} />
    </>
  );

  // The phone picks from a bottom sheet, as its other pickers do: a 280px
  // popover of 36px rows is a desktop control.
  if (noAutoFocus) {
    return (
      <>
        <Flex
          as="button"
          type="button"
          w="100%"
          minH="52px"
          // The native selects' own inset, so this lines up with Tipo under it.
          px={4}
          align="center"
          justify="space-between"
          textAlign="left"
          borderRadius="12px"
          border="1px solid"
          borderColor="redesign.border"
          bg="redesign.bg.surface"
          onClick={onOpen}
        >
          {boxedLabel}
        </Flex>
        <SourceSheet
          isOpen={isOpen}
          onClose={onClose}
          sources={sources}
          value={valueId}
          onChange={onSelect}
          onCreate={onCreate}
          glyph={(src) => <SourceGlyph source={src} boxSize="22px" />}
        />
      </>
    );
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={close}
      placement="bottom-start"
      initialFocusRef={noAutoFocus ? undefined : searchRef}
    >
      <PopoverTrigger>
        {boxed ? (
          /* Create screen: a full-width select box, matching TypePicker. */
          <Flex
            align="center"
            justify="space-between"
            cursor="pointer"
            px={3}
            py={2}
            borderRadius="lg"
            border="1px solid"
            borderColor="redesign.border"
            bg="redesign.bg.surface"
            _hover={{ bg: 'redesign.bg.hover' }}
          >
            {boxedLabel}
          </Flex>
        ) : (
          <Flex align="center" gap={2} cursor="pointer" _hover={{ opacity: 0.8 }} minH={noAutoFocus ? '44px' : '28px'}>
            {selected
              ? triggerTag(<><SourceGlyph source={selected} />{selected.name}</>)
              /* Legacy free-text value, on an account not yet linked to an entity. */
              : valueName
                ? triggerTag(valueName)
                : (
                  <Text fontSize="sm" color="redesign.text.muted">
                    {t('detail.fields.sourcePlaceholder')}
                  </Text>
                )}
          </Flex>
        )}
      </PopoverTrigger>
      <PopoverContent w="280px" boxShadow="lg" borderRadius="lg">
        <PopoverBody p={0}>
          <Box p={2} borderBottom="1px solid" borderColor="redesign.border">
            <InputGroup size="sm">
              <InputLeftElement>
                <SearchIcon color="redesign.text.muted" />
              </InputLeftElement>
              <Input
                ref={searchRef}
                placeholder={t('detail.fields.sourceSearch')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                variant="filled"
                borderRadius="md"
                _focus={{ bg: 'redesign.bg.card' }}
              />
            </InputGroup>
          </Box>

          <Box maxH="220px" overflowY="auto" py={1}>
            {(valueId || valueName) && (
              <Flex
                px={3} py={2} cursor="pointer" borderRadius="md"
                _hover={{ bg: 'redesign.bg.hover' }}
                onClick={() => { onSelect(null); close(); }}
              >
                <Text fontSize="sm" color="redesign.text.muted">
                  {t('detail.fields.sourceNone')}
                </Text>
              </Flex>
            )}

            {filtered.map(src => (
              <Flex
                key={src.id}
                px={3} py={2} cursor="pointer" align="center" gap={2}
                bg={src.id === valueId ? 'redesign.bg.accent' : 'transparent'}
                _hover={{ bg: src.id === valueId ? 'redesign.bg.accent' : 'redesign.bg.hover' }}
                onClick={() => { onSelect(src); close(); }}
              >
                <SourceGlyph source={src} />
                <Text fontSize="md" fontWeight={src.id === valueId ? '600' : '400'}>
                  {src.name}
                </Text>
              </Flex>
            ))}

            {canCreate && (
              <Flex
                px={3} py={2} cursor="pointer" borderRadius="md"
                _hover={{ bg: 'redesign.bg.hover' }}
                onClick={handleCreate}
              >
                <Text fontSize="sm" color="redesign.accent.blue">
                  {creating ? '…' : t('detail.fields.sourceCreate', { name: search.trim() })}
                </Text>
              </Flex>
            )}

            {!filtered.length && !canCreate && (
              <Text fontSize="sm" px={3} py={2} color="redesign.text.muted">
                {t('detail.fields.sourceEmpty')}
              </Text>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

// MI-340: top-level "Tipo" control (Bancos/Inversiones/Activos), paired with
// a Subtipo tab row below it, so the live preview card always has a definite
// subtype to derive a color from.
function TypePicker({ categoryIndex, onSelect, native = false }) {
  const { t } = useTranslation('accounts');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const category = GROUP_CATEGORIES[categoryIndex];

  // The phone: three options is a native select, which gets iOS's own picker.
  if (native) {
    return (
      <Select
        value={categoryIndex}
        onChange={(e) => onSelect(Number(e.target.value))}
        h="52px"
        borderRadius="12px"
        borderColor="redesign.border"
        bg="redesign.bg.surface"
        aria-label={t('detail.fields.type')}
      >
        {GROUP_CATEGORIES.map((cat, i) => (
          <option key={cat.labelKey} value={i}>{t(`categories.${cat.labelKey}`)}</option>
        ))}
      </Select>
    );
  }

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Flex
          align="center"
          justify="space-between"
          cursor="pointer"
          px={3}
          py={2}
          borderRadius="lg"
          border="1px solid"
          borderColor="redesign.border"
          bg="redesign.bg.surface"
          _hover={{ bg: 'redesign.bg.hover' }}
        >
          <Text fontSize="sm" color="redesign.text">{t(`categories.${category.labelKey}`)}</Text>
          <ChevronDownIcon color="redesign.text.muted" boxSize={4} />
        </Flex>
      </PopoverTrigger>
      <PopoverContent w="220px" boxShadow="lg" borderRadius="lg">
        <PopoverBody p={1}>
          {GROUP_CATEGORIES.map((cat, i) => (
            <Flex
              key={cat.labelKey}
              px={3}
              py={2}
              cursor="pointer"
              borderRadius="md"
              bg={i === categoryIndex ? 'redesign.bg.accent' : 'transparent'}
              _hover={{ bg: 'redesign.bg.hover' }}
              onClick={() => { onSelect(i); onClose(); }}
            >
              <Text fontSize="sm" fontWeight={i === categoryIndex ? 600 : 400} color="redesign.text">
                {t(`categories.${cat.labelKey}`)}
              </Text>
            </Flex>
          ))}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

// "Belongs to": the broker a stock or funds account sits under, or the person
// a loan sits under. A boxed popover like TypePicker, with a search box once
// the list is long; the phone gets a native select.
function ParentPicker({ value, options, onSelect, native = false }) {
  const { t } = useTranslation('accounts');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');
  const selected = options.find(a => a.id === value);
  const q = search.trim().toLowerCase();
  const shown = q ? options.filter(a => a.name.toLowerCase().includes(q)) : options;
  const close = () => { onClose(); setSearch(''); };
  const pick = (id) => { onSelect(id); close(); };

  if (native) {
    return (
      <Select
        value={value ?? ''}
        onChange={(e) => onSelect(e.target.value ? Number(e.target.value) : null)}
        h="52px"
        borderRadius="12px"
        borderColor="redesign.border"
        bg="redesign.bg.surface"
        aria-label={t('detail.fields.belongsTo')}
      >
        <option value="">{t('detail.fields.belongsToNone')}</option>
        {options.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </Select>
    );
  }

  const option = (id, label, active) => (
    <Flex
      key={id ?? 'none'}
      px={3}
      py={2}
      cursor="pointer"
      borderRadius="md"
      bg={active ? 'redesign.bg.accent' : 'transparent'}
      _hover={{ bg: 'redesign.bg.hover' }}
      onClick={() => pick(id)}
    >
      <Text fontSize="sm" fontWeight={active ? 600 : 400} color={id == null ? 'redesign.text.muted' : 'redesign.text'}>
        {label}
      </Text>
    </Flex>
  );

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={close} placement="bottom-start">
      <PopoverTrigger>
        <Flex
          align="center"
          justify="space-between"
          cursor="pointer"
          px={3}
          py={2}
          borderRadius="lg"
          border="1px solid"
          borderColor="redesign.border"
          bg="redesign.bg.surface"
          _hover={{ bg: 'redesign.bg.hover' }}
        >
          <Text fontSize="sm" color={selected ? 'redesign.text' : 'redesign.text.muted'} noOfLines={1}>
            {selected ? selected.name : t('detail.fields.belongsToNone')}
          </Text>
          <ChevronDownIcon color="redesign.text.muted" boxSize={4} />
        </Flex>
      </PopoverTrigger>
      <PopoverContent w="260px" boxShadow="lg" borderRadius="lg">
        <PopoverBody p={1}>
          {options.length > 8 && (
            <InputGroup size="sm" mb={1}>
              <InputLeftElement>
                <SearchIcon color="redesign.text.muted" />
              </InputLeftElement>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('detail.fields.belongsToSearch')}
                variant="filled"
                borderRadius="md"
              />
            </InputGroup>
          )}
          <Box maxH="220px" overflowY="auto">
            {option(null, t('detail.fields.belongsToNone'), value == null)}
            {shown.map(a => option(a.id, a.name, a.id === value))}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

// The phone's "+" beside the currencies: a 44px target that opens the
// currency sheet, instead of the desktop's 28px trigger and popover.
function PhoneAddCurrency({ remaining, onAdd }) {
  const { t } = useTranslation('accounts');
  const [isOpen, setIsOpen] = useState(false);
  if (remaining.length === 0) return null;
  return (
    <>
      <Box
        as="button"
        type="button"
        w="44px"
        h="44px"
        borderRadius="full"
        border="1px dashed"
        borderColor="redesign.text.muted"
        color="redesign.text.muted"
        display="flex"
        alignItems="center"
        justifyContent="center"
        lineHeight="1"
        fontSize="xl"
        onClick={() => setIsOpen(true)}
        aria-label={t('detail.fields.addCurrency')}
      >
        +
      </Box>
      <CurrencySheet
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        currencies={remaining}
        onChange={onAdd}
      />
    </>
  );
}

// A stacked label-above-control field, the create screen's layout rhythm
// (distinct from the edit screen's left-aligned-label rows).
function CreateField({ label, children, ...rest }) {
  return (
    <Box mb={4} {...rest}>
      <CreateLabel>{label}</CreateLabel>
      {children}
    </Box>
  );
}

function CreateLabel({ children, ...rest }) {
  return (
    <Text fontSize="sm" fontWeight={600} color="redesign.text" mb={2} {...rest}>
      {children}
    </Text>
  );
}

// New, purpose-built accent palette for the account color picker (MI-340) —
// independent of Category.color, same relationship as `icon` (a free string,
// this component chooses the offered set).
const ACCOUNT_COLOR_SWATCHES = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E', '#10B981', '#14B8A6',
  '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#EC4899', '#F43F5E', '#64748B',
];

const emptyAccount = (currencies) => ({
  name: '', group: null,
  // Defaults to Bancos > Ahorros — matches the reviewed design mockup, and
  // keeps the live preview card's gradient always defined from the start.
  account_type: 'saving',
  icon: '', note: '', external_account_number: '', color: null,
  currencies: currencies || [], is_active: true, source: '', source_id: null,
});

// Reusable account create/edit form. Used by both the account detail side panel
// and the onboarding wizard, so the wizard captures a real account (all types,
// currencies, opening balance, note) instead of a stripped-down mini-form.
//
// Props:
//   account          existing account (edit) or null/undefined (create)
//   initialCurrencies currency codes to pre-seed in create mode (e.g. the ones
//                     the onboarding country step activated)
//   onSaved(result)  called with the saved account after a successful save
//   onCancel         if provided, renders a Cancel button that calls it
//   embedded         the form is already inside a padded container (onboarding's
//                    card), so it drops its own gutter and width cap
//   compact          onboarding's layout: two columns, no icon/note, one row of
//                    swatches, and no submit button of its own — the card's
//                    footer owns it. The side panel never passes this.
//   submitRef        a ref the caller fills with this form's save(), so a button
//                    outside it can fire the save
//   onReadyChange    reports { canSave, saving } so that outside button can be
//                    enabled and show progress. Must be stable (useCallback).
//   noAutoFocus      a phone: nothing takes focus by itself, because iOS would
//                    raise the keyboard before the person has tapped a field,
//                    touch targets go to 44px, the pickers are bottom sheets or
//                    native selects, and the balance reads the separators the
//                    phone's decimal pad types
export default function AccountForm({
  account, initialCurrencies, onSaved, onCancel, embedded = false,
  compact = false, submitRef, onReadyChange, noAutoFocus = false,
}) {
  const { t } = useTranslation('accounts');
  const isCreateMode = !account?.id;
  const toast = useToast();

  const [editedAccount, setEditedAccount] = useState(
    () => account || emptyAccount(initialCurrencies)
  );
  const [isEditingNote, setIsEditingNote] = useState(false);
  // Whether the opening balance is the phone's text field, fixed when the
  // form mounts. The field and the way it is read must stay a pair: a window
  // resized across the breakpoint keeps the form mounted, and a '1.500.000'
  // typed on the phone would otherwise be read by the computer's parseFloat.
  const [textBalance] = useState(noAutoFocus);
  const noteRef = useRef(null);
  // A field that only exists once tapped. Without autofocus, focusing it in the
  // same tap is what makes iOS raise the keyboard instead of asking for a
  // second one — hence flushSync, so the input is mounted before focus().
  const startEditingNote = () => {
    if (!noAutoFocus) { setIsEditingNote(true); return; }
    flushSync(() => setIsEditingNote(true));
    noteRef.current?.focus();
  };
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [sources, setSources] = useState([]);
  // Every account of the workspace, for the "Belongs to" picker.
  const [allAccounts, setAllAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Raw per-currency opening-balance inputs, shown as positive figures
  // (debt is negated for display / storage). Keyed by currency code.
  const [openingInputs, setOpeningInputs] = useState({});
  // Per-currency anchor dates (YYYY-MM-DD). Empty → backend anchors before the
  // first movement. Keyed by currency code.
  const [openingDates, setOpeningDates] = useState({});

  useEffect(() => {
    fetchCurrencies(true).then(setAvailableCurrencies).catch(() => {});
    // MI-304: the institution catalog — seeded globals + workspace-custom rows.
    // Previously derived from the user's own accounts, which left a brand-new
    // user with nothing to pick.
    fetchSources().then(setSources).catch(() => {});
    getReferenceData().then((data) => setAllAccounts(data.accounts)).catch(() => {});
  }, []);


  // Seed the opening-balance inputs (as positive figures) from the account.
  useEffect(() => {
    const debt = isDebtAccount(
      editedAccount.account_group || editedAccount.group,
      editedAccount.account_type
    );
    const seeded = {};
    const seededDates = {};
    (editedAccount.opening_balances || []).forEach(o => {
      const shown = debt ? -o.amount : o.amount;
      seeded[o.currency] = shown ? String(shown) : '';
      seededDates[o.currency] = o.as_of_date || '';
    });
    setOpeningInputs(seeded);
    setOpeningDates(seededDates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  const handleFieldChange = (field, value) => {
    setEditedAccount(prev => ({ ...prev, [field]: value }));
  };

  // Build the opening_balances payload from the per-currency inputs. Debt
  // accounts are entered as positive figures and stored negative. Only send a
  // currency when the user typed something or a row already existed (so
  // clearing it resets to 0).
  const buildOpeningBalances = () => {
    const debt = isDebtAccount(
      editedAccount.account_group || editedAccount.group,
      editedAccount.account_type
    );
    return (editedAccount.currencies || [])
      .filter(cur => {
        const raw = openingInputs[cur];
        const had = (editedAccount.opening_balances || []).some(o => o.currency === cur);
        return (raw !== undefined && raw !== '') || had;
      })
      .map(currency => {
        // The phone's field is text: its decimal pad types a Colombian
        // `1.500.000`, which parseFloat would read as 1.5. A balance can be
        // negative (an overdraft), in any of the ways a paste writes it.
        const num = textBalance
          ? (parseSignedAmount(openingInputs[currency]) ?? NaN)
          : parseFloat(openingInputs[currency]);
        const shown = Number.isNaN(num) ? 0 : num;
        const row = { currency, amount: debt ? -shown : shown };
        // Optional anchor date; omit it so the backend defaults to the day
        // before the first movement.
        if (openingDates[currency]) row.as_of_date = openingDates[currency];
        return row;
      });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editedAccount.name,
        group: editedAccount.group || undefined,
        account_type: editedAccount.account_type || undefined,
        note: editedAccount.note || '',
        external_account_number: editedAccount.external_account_number || '',
        source: editedAccount.source ?? '',
        source_id: editedAccount.source_id ?? null,
        icon: editedAccount.icon || '',
        color: editedAccount.color || null,
        currencies: editedAccount.currencies || [],
        opening_balances: buildOpeningBalances(),
      };
      // A holding or a loan names its parent; any other type has none, so an
      // account moved to another type leaves the parent it had.
      if (parentType) {
        payload.parent_account_id = parentOptions.some(a => a.id === editedAccount.parent_account_id)
          ? editedAccount.parent_account_id
          : null;
      } else if (account?.parent_account_id) {
        payload.parent_account_id = null;
      }
      if (isInstrument) payload.instrument_symbol = editedAccount.instrument_symbol?.trim() || null;

      let result;
      if (isCreateMode) {
        result = await createAccount(payload);
        setSaved(true);
      } else {
        result = await updateAccount(account.id, { ...payload, is_active: editedAccount.is_active });
      }

      toast({
        title: t('detail.toasts.successTitle'),
        description: isCreateMode ? t('detail.toasts.createSuccess') : t('detail.toasts.updateSuccess'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (onSaved) onSaved(result);
    } catch (err) {
      console.error(isCreateMode ? 'Error creating account:' : 'Error updating account:', err);
      toast({
        title: t('detail.toasts.errorTitle'),
        description: isCreateMode ? t('detail.toasts.createError') : t('detail.toasts.updateError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // MI-340 — derived for the create-mode live preview card. Computed
  // unconditionally (hooks can't be called inside the isCreateMode branch
  // below) but only rendered in create mode.
  const activeCategoryIndex = Math.max(0, GROUP_CATEGORIES.findIndex(
    cat => cat.groups.some(g => g.value === editedAccount.account_type)
  ));
  const activeCategory = GROUP_CATEGORIES[activeCategoryIndex];
  const activeSubtype = activeCategory.groups.find(g => g.value === editedAccount.account_type)
    || activeCategory.groups[0];
  const cardBackground = editedAccount.color
    || `linear-gradient(135deg, ${activeSubtype.from}, ${activeSubtype.to})`;
  // The solid end of that gradient, or the colour the user picked. The mockup
  // paints the step's own Continue button with it, so the account being made
  // is what the button looks like.
  const buttonAccent = editedAccount.color || activeSubtype.to;
  // The card's text: white where it reads, a dark ink on the light cards
  // (white on the savings green is 1.7:1). Judged on every colour the text
  // crosses: the swatch, or both stops of the gradient.
  const cardInk = editedAccount.color
    ? readableInk(editedAccount.color)
    : readableInk(activeSubtype.from, activeSubtype.to);
  // Placeholders and the dots, a step softer than the ink: at 0.8 they still
  // read on the mid-tone cards, where 0.6-0.7 fell to 2-3:1.
  const cardInkSoft = cardInk === 'white' ? 'whiteAlpha.800' : 'blackAlpha.800';
  const cardInkFaint = cardInkSoft;
  const selectedSource = sources.find(s => s.id === editedAccount.source_id);
  const selectedSourceName = selectedSource?.name || editedAccount.source;
  // MI-345: an institution with a logo is the account's picture, so the form
  // offers its own icon only when there is none (cash, a person, a custom bank).
  const offersIcon = !selectedSource?.logo_url;

  const currencies = editedAccount.currencies || [];
  const primaryCurrency = currencies[0];
  // "Belongs to": a stock or funds account under a broker, a loan under a
  // person. The options are the accounts of the parent type that have no
  // parent themselves; an account that already holds others cannot belong to one.
  const parentType = PARENT_TYPE[editedAccount.account_type];
  const isInstrument = editedAccount.account_type === 'stock' || editedAccount.account_type === 'funds';
  const hasChildren = Boolean(account?.id) && allAccounts.some(a => a.parent_account_id === account.id);
  const parentOptions = parentType && !hasChildren
    ? allAccounts.filter(a => a.account_type === parentType && a.parent_account_id == null && a.id !== account?.id)
    : [];
  const isDebt = isDebtAccount(
    editedAccount.account_group || editedAccount.group,
    editedAccount.account_type
  );
  // Onboarding draws the submit button in the card's own footer, so it needs a
  // way to fire this form's save and to know when the form is ready. Reassigned
  // on every render because `handleSave` closes over current state.
  const canSave = !saving && !saved
    && !!editedAccount?.name?.trim()
    && !!editedAccount?.currencies?.length
    && !!editedAccount?.account_type;

  useEffect(() => {
    if (submitRef) submitRef.current = handleSave;
  });

  useEffect(() => {
    onReadyChange?.({ canSave, saving, accent: buttonAccent });
  }, [canSave, saving, buttonAccent, onReadyChange]);

  const balanceLabel = isDebt
    ? t('detail.fields.startingDebt')
    : t('detail.fields.openingBalance');

  // Create and edit are the same screen. Editing adds the anchor dates for
  // every currency and the active switch; creating keeps one balance field.
  return (
      // Capped width: the detail panel is user-resizable and can get much
      // wider than this form is meant to be read at. A card sets its own
      // measure, so an embedded form indents and caps nothing itself.
      <Box
        px={embedded ? 0 : { base: 4, md: 7 }}
        py={embedded ? 0 : { base: 3, md: 5 }}
        maxW={embedded ? 'full' : '520px'}
      >
        <CreateField label={t('detail.fields.source')}>
          <SourcePicker
            boxed
            noAutoFocus={noAutoFocus}
            valueId={editedAccount.source_id}
            valueName={editedAccount.source}
            sources={sources}
            onSelect={(src) => setEditedAccount(prev => ({
              ...prev,
              source_id: src ? src.id : null,
              source: src ? src.name : '',
            }))}
            onCreate={async (name) => {
              const created = await createSource({ name });
              setSources(prev =>
                [...prev, created].sort((a, b) => a.name.localeCompare(b.name))
              );
              setEditedAccount(prev => ({
                ...prev, source_id: created.id, source: created.name,
              }));
            }}
          />
        </CreateField>

        <CreateField label={t('detail.fields.type')}>
          <TypePicker
            native={noAutoFocus}
            categoryIndex={activeCategoryIndex}
            onSelect={(i) => handleFieldChange('account_type', GROUP_CATEGORIES[i].groups[0].value)}
          />
        </CreateField>

        <CreateField label={t('detail.fields.subtype')}>
          <Flex bg="redesign.bg.sunken" border="1px solid" borderColor="redesign.border" borderRadius="lg" p={1} gap={1}>
            {activeCategory.groups.map(g => {
              const active = g.value === editedAccount.account_type;
              return (
                <Box
                  key={g.value}
                  as="button"
                  type="button"
                  flex="1"
                  textAlign="center"
                  fontSize="sm"
                  fontWeight={600}
                  py={2}
                  minH={noAutoFocus ? '44px' : undefined}
                  borderRadius="md"
                  bg={active ? 'redesign.bg.raised' : 'transparent'}
                  color={active ? 'redesign.heading' : 'redesign.text.muted'}
                  boxShadow={active ? 'sm' : 'none'}
                  onClick={() => handleFieldChange('account_type', g.value)}
                >
                  {t(`types.${g.labelKey}`)}
                </Box>
              );
            })}
          </Flex>
        </CreateField>

        {/* Live preview card */}
        <Box
          borderRadius="2xl"
          p={5}
          minH="132px"
          background={cardBackground}
          color={cardInk}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          mb={2}
          transition="background 0.2s"
        >
          <Text fontSize="lg" fontWeight="700" noOfLines={1}>
            {selectedSourceName || t('detail.fields.sourcePlaceholder')}
          </Text>
          <Flex justify="space-between" align="baseline" gap={3}>
            <Input
              value={editedAccount.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              placeholder={t('detail.fields.namePlaceholder')}
              variant="unstyled"
              color={cardInk}
              fontSize="sm"
              fontWeight="600"
              minW={0}
              _placeholder={{ color: cardInkSoft }}
            />
            <Flex align="baseline" gap={1} flexShrink={0}>
              <Text as="span" color={cardInkSoft} letterSpacing="1px">••</Text>
              <Input
                value={editedAccount.external_account_number || ''}
                onChange={(e) => handleFieldChange('external_account_number', e.target.value)}
                placeholder={t('detail.fields.externalAccountNumberPlaceholder')}
                variant="unstyled"
                color={cardInk}
                fontSize="sm"
                fontWeight="600"
                textAlign="right"
                w={noAutoFocus ? '64px' : '90px'}
                _placeholder={{ color: cardInkFaint }}
                aria-label={t('detail.fields.externalAccountNumber')}
              />
            </Flex>
          </Flex>
        </Box>
        {!editedAccount.name?.trim() && (
          <Text fontSize="xs" color={noAutoFocus ? 'phone.alert' : 'redesign.text.warn'} mt="-6px" mb={4}>
            {t('detail.fields.nameRequiredHint')}
          </Text>
        )}

        {parentOptions.length > 0 && (
          <CreateField label={t('detail.fields.belongsTo')}>
            <ParentPicker
              native={noAutoFocus}
              value={editedAccount.parent_account_id ?? null}
              options={parentOptions}
              onSelect={(id) => handleFieldChange('parent_account_id', id)}
            />
          </CreateField>
        )}

        {isInstrument && (
          <CreateField label={t('detail.fields.symbol')}>
            <Input
              value={editedAccount.instrument_symbol || ''}
              onChange={(e) => handleFieldChange('instrument_symbol', e.target.value.toUpperCase())}
              placeholder={t('detail.fields.symbolPlaceholder')}
              borderRadius="lg"
              {...(noAutoFocus && { h: '52px', borderRadius: '12px' })}
            />
          </CreateField>
        )}

        {/* Opening balance. The primary currency while creating; every
            currency, with its anchor date, once the account exists. */}
        {(isCreateMode ? [primaryCurrency].filter(Boolean) : currencies).length > 0 && (
          <Box mb={4}>
            <Text
              textStyle="microLabel"
              mb={1}
              // The phone's caption: 11px, and a grey that reads (the faint
              // one is about 1.4:1 on the page).
              {...(noAutoFocus && { fontSize: '11px', lineHeight: '14px', color: 'phone.muted' })}
            >
              {balanceLabel}
            </Text>
            {(isCreateMode ? [primaryCurrency] : currencies).map(cur => (
              <Flex key={cur} align="center" gap={3} mb={2}>
                <Text fontSize="md" fontWeight={600} color="redesign.text.muted">{cur}</Text>
                <Input
                  variant="unstyled"
                  type={textBalance ? 'text' : 'number'}
                  inputMode="decimal"
                  // Past 16px already, so the phone's field net may leave it.
                  {...(textBalance && { autoComplete: 'off', 'data-display-size': '' })}
                  // The default placeholder grey is about 2:1 in dark mode.
                  {...(noAutoFocus && { _placeholder: { color: 'phone.muted' } })}
                  fontSize="2xl"
                  fontWeight={700}
                  color="redesign.heading"
                  flex="1"
                  minW={0}
                  value={openingInputs[cur] ?? ''}
                  onChange={(e) => setOpeningInputs(prev => ({ ...prev, [cur]: e.target.value }))}
                  placeholder="0"
                />
                {!isCreateMode && (
                  <Input
                    size="sm"
                    type="date"
                    w="150px"
                    flexShrink={0}
                    borderRadius="lg"
                    value={openingDates[cur] ?? ''}
                    onChange={(e) => setOpeningDates(prev => ({ ...prev, [cur]: e.target.value }))}
                    aria-label={t('detail.fields.openingDateAria', { currency: cur })}
                  />
                )}
              </Flex>
            ))}
            {!isCreateMode && (
              <Text fontSize="xs" color="redesign.text.muted" mt={1}>
                {t('detail.fields.openingDateHint')}
              </Text>
            )}
          </Box>
        )}

        {/* On the phone every swatch is 44px tall, and eight to a row only
            while that keeps them 44px wide: a narrower column (a 320px screen,
            where they would be 36px) takes four to a row instead. */}
        <Box mb={4} sx={noAutoFocus ? { containerType: 'inline-size' } : undefined}>
          <SimpleGrid
            columns={8}
            spacing={0}
            borderRadius={compact ? '12px' : 'lg'}
            overflow="hidden"
            sx={noAutoFocus ? {
              '@container (max-width: 351.98px)': { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
            } : undefined}
          >
            {ACCOUNT_COLOR_SWATCHES.map(hex => {
              const selected = hex === editedAccount.color;
              return (
                <Box
                  as="button"
                  type="button"
                  key={hex}
                  aspectRatio={noAutoFocus ? undefined : '1'}
                  h={noAutoFocus ? '44px' : undefined}
                  bg={hex}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  onClick={() => handleFieldChange('color', selected ? null : hex)}
                  aria-label={t('detail.fields.colorOptionAria', { hex })}
                  aria-pressed={selected}
                >
                  {selected && <CheckIcon color={readableInk(hex)} boxSize={3} />}
                </Box>
              );
            })}
          </SimpleGrid>
        </Box>

        {/* Currencies */}
        <Box mb={4}>
          <CreateLabel>{t('detail.fields.currencies')}</CreateLabel>
          <Flex gap={2} align="center" flexWrap="wrap">
            {(editedAccount.currencies || []).map(c => (
              <Tag
                key={c}
                size="md"
                bg="redesign.bg.accent"
                color="redesign.accent.blue"
                borderRadius="full"
                px={3}
                py={1}
                fontWeight={600}
                // The phone: a 44px chip whose ✕ fills a 44px end of it.
                {...(noAutoFocus && { h: '44px', pl: 4, pr: 0, py: 0, fontSize: '16px' })}
              >
                {c}
                <IconButton
                  icon={<SmallCloseIcon />}
                  size="xs"
                  variant="ghost"
                  {...(noAutoFocus
                    ? { minW: '44px', h: '44px', ml: 0, borderRadius: 'full' }
                    : { minW: 'auto', h: 'auto', ml: 1 })}
                  onClick={() => handleFieldChange('currencies', editedAccount.currencies.filter(x => x !== c))}
                  aria-label={t('detail.fields.removeCurrencyAria', { currency: c })}
                />
              </Tag>
            ))}
          {noAutoFocus ? (
            <PhoneAddCurrency
              remaining={availableCurrencies.filter(c => !(editedAccount.currencies || []).includes(c.name))}
              onAdd={(code) => handleFieldChange('currencies', [...(editedAccount.currencies || []), code])}
            />
          ) : (
          <Popover placement="bottom-start">
            {({ onClose: closeCurrPopover }) => {
              const selected = editedAccount.currencies || [];
              const remaining = availableCurrencies.filter(c => !selected.includes(c.name));
              return (
                <>
                  <PopoverTrigger>
                    <Box
                      as="button"
                      type="button"
                      w="28px"
                      h="28px"
                      borderRadius="full"
                      border="1px dashed"
                      borderColor="redesign.text.faint"
                      color="redesign.text.muted"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      lineHeight="1"
                      fontSize="md"
                      _hover={{ borderColor: 'redesign.accent.blue', color: 'redesign.accent.blue', bg: 'redesign.bg.hover' }}
                      aria-label={t('detail.fields.addCurrency')}
                    >
                      +
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent w="160px" boxShadow="lg" borderRadius="lg">
                    <PopoverBody p={1} maxH="200px" overflowY="auto">
                      {remaining.length === 0 ? (
                        <Text fontSize="xs" color="redesign.text.muted" p={2}>{t('detail.fields.noMoreCurrencies')}</Text>
                      ) : remaining.map(c => (
                        <Flex
                          key={c.name}
                          px={3}
                          py={2}
                          cursor="pointer"
                          borderRadius="md"
                          _hover={{ bg: 'redesign.bg.hover' }}
                          onClick={() => {
                            handleFieldChange('currencies', [...selected, c.name]);
                            closeCurrPopover();
                          }}
                        >
                          <Text fontSize="sm">{c.name}</Text>
                        </Flex>
                      ))}
                    </PopoverBody>
                  </PopoverContent>
                </>
              );
            }}
          </Popover>
          )}
          </Flex>
        </Box>

        {/* Icon + Note. Onboarding leaves both out: they are what a settled
            user adds later from the panel, and on a first account they are two
            more fields between the person and their first movement. */}
        {!compact && offersIcon && (
        <Box mb={4}>
          <CreateLabel>
            {t('detail.fields.icon')}{' '}
            <Text as="span" color="redesign.text.muted" fontWeight={400}>
              {t('detail.fields.optionalSuffix')}
            </Text>
          </CreateLabel>
          <EmojiPickerField
            value={editedAccount.icon || ''}
            onChange={(emoji) => handleFieldChange('icon', emoji)}
            placeholder="🖼️"
            boxSize="44px"
          />
        </Box>
        )}

        {!compact && (
        <Box mb={4}>
          <CreateLabel>
            {t('detail.fields.note')}{' '}
            <Text as="span" color="redesign.text.muted" fontWeight={400}>
              {t('detail.fields.optionalSuffix')}
            </Text>
          </CreateLabel>
          {isEditingNote ? (
            <Textarea
              ref={noteRef}
              value={editedAccount.note || ''}
              onChange={(e) => handleFieldChange('note', e.target.value)}
              onBlur={() => setIsEditingNote(false)}
              autoFocus={!noAutoFocus}
              // A note runs over lines, so Return stays Return.
              enterKeyHint={noAutoFocus ? 'enter' : undefined}
              placeholder={t('detail.fields.notePlaceholder')}
              size="sm"
              rows={3}
              resize="vertical"
              borderRadius="lg"
            />
          ) : (
            /* Click-to-edit, but drawn as a field so it reads like one. */
            <Text
              fontSize="sm"
              color={editedAccount.note ? 'redesign.text' : 'redesign.text.muted'}
              whiteSpace="pre-wrap"
              onClick={startEditingNote}
              cursor="text"
              px={3}
              py={2}
              borderRadius="lg"
              border="1px solid"
              borderColor="redesign.border"
              bg="redesign.bg.surface"
              _hover={{ bg: 'redesign.bg.hover' }}
              minH={noAutoFocus ? '44px' : '40px'}
            >
              {editedAccount.note || t('detail.fields.addNote')}
            </Text>
          )}
        </Box>
        )}

        {!isCreateMode && (
          <Box mb={4}>
            <CreateLabel>{t('detail.fields.status')}</CreateLabel>
            <Switch
              isChecked={editedAccount.is_active}
              onChange={() => handleFieldChange('is_active', !editedAccount.is_active)}
              aria-label={t('detail.fields.statusAria')}
            />
          </Box>
        )}

        {/* Onboarding renders the submit in the card's footer instead, so the
            step shows one button for one action rather than two. */}
        {!compact && (
          <Flex gap={2}>
            <Button
              variant="primary"
              flex={1}
              onClick={handleSave}
              isLoading={saving}
              isDisabled={!canSave}
            >
              {isCreateMode ? t('detail.buttons.create') : t('detail.buttons.save')}
            </Button>
            {onCancel && (
              <Button variant="outline" onClick={onCancel}>
                {t('detail.buttons.cancel')}
              </Button>
            )}
          </Flex>
        )}
      </Box>
  );
}

